import { requestText } from "../i18n/requestText";
import { systemParenText } from "../i18n/systemParenText";
import type { FieldError, Locale, Problem, UnmetRule } from "./types";

/** Problem type suffixes from the contract (RFC 7807 `type`). */
const FEATURE_DISABLED = "/feature-disabled";

export const NETWORK_MESSAGE = "Giraffe cannot be reached. Check your connection and try again.";

/** Retry-After as seconds: delta-seconds or an HTTP date; undefined when absent or unreadable. */
export function parseRetryAfter(value: string | null | undefined, now = Date.now()): number | undefined {
  if (value === null || value === undefined || value.trim() === "") return undefined;
  if (/^\d+$/.test(value.trim())) return Number(value.trim());
  const at = Date.parse(value);
  return Number.isNaN(at) ? undefined : Math.max(0, Math.ceil((at - now) / 1000));
}

/**
 * A failed API call. `status` 0 means the request never reached the server.
 * Screens branch on the helpers below; they never string-match `title` or `detail`.
 */
export class ApiProblem extends Error {
  readonly status: number;
  readonly problem: Problem | undefined;
  /** Seconds from the response's Retry-After header, when it had one. */
  readonly retryAfterSeconds: number | undefined;
  /** The permission matrix revision a 403 was decided at (`Giraffe-Permissions-Revision`, contract 0.10.36), when sent. */
  readonly permissionsRevision: number | undefined;
  /** The response's Content-Type, when it had one. Giraffe itself always answers an error as problem+json. */
  readonly contentType: string | undefined;

  constructor(
    status: number,
    problem?: Problem,
    fallback?: string,
    retryAfter?: string | null,
    permissionsRevision?: string | null,
    contentType?: string | null,
  ) {
    super(problem?.detail ?? problem?.title ?? fallback ?? `Request failed (${status})`);
    this.name = "ApiProblem";
    this.status = status;
    this.problem = problem;
    this.retryAfterSeconds = parseRetryAfter(retryAfter);
    const revision = permissionsRevision == null || permissionsRevision === "" ? Number.NaN : Number(permissionsRevision);
    this.permissionsRevision = Number.isInteger(revision) && revision >= 0 ? revision : undefined;
    this.contentType = contentType ?? undefined;
  }

  /**
   * A 403 that did not come from Giraffe: no problem body and not JSON (an HTML page). On PRD this is the host's network
   * filter, which refuses any write whose text holds "System (" (ruling 10 Oct 2026, afternoon). Giraffe's own 403s
   * (permission, csrf-invalid, ui-only-action, segregation of duties) are always problem+json and never land here.
   */
  get isProxyBlocked(): boolean {
    return isProxyBlockResponse(this.status, this.problem, this.contentType);
  }

  /** 409 with Retry-After: the original request with this Idempotency-Key is still in progress (SA-01 §5). */
  get isInProgress(): boolean {
    return this.status === 409 && this.retryAfterSeconds !== undefined;
  }

  get type(): string {
    return this.problem?.type ?? "";
  }

  /** Problem.code (R17; closed set from R21), e.g. not-found, segregation-of-duties, csrf-invalid. */
  get code(): string {
    const code = (this.problem as { code?: unknown } | undefined)?.code;
    return typeof code === "string" ? code : "";
  }

  get isNetwork(): boolean {
    return this.status === 0;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** 400 bad-request, e.g. a list cursor from an earlier list order (SA-01 §5.1n R28): restart the list. */
  get isBadRequest(): boolean {
    return this.status === 400 && this.code === "bad-request";
  }

  /** 404 feature-disabled: render the flag's defined off-state (UX-01 4.12), never "not found". */
  get isFeatureDisabled(): boolean {
    return this.status === 404 && (this.code === "feature-disabled" || this.type.endsWith(FEATURE_DISABLED));
  }

  get featureFlag(): string | undefined {
    const flag = (this.problem as { flag?: unknown } | undefined)?.flag;
    return typeof flag === "string" ? flag : undefined;
  }

  /** Unmet transition rules (422 UnmetRulesProblem), each already in words. */
  get unmetRules(): UnmetRule[] {
    const rules = (this.problem as { unmetRules?: unknown } | undefined)?.unmetRules;
    return Array.isArray(rules) ? rules.filter(isUnmetRule) : [];
  }

  get fieldErrors(): FieldError[] {
    return this.problem?.errors ?? [];
  }
}

function isUnmetRule(value: unknown): value is UnmetRule {
  return typeof value === "object" && value !== null && typeof (value as UnmetRule).message === "string";
}

/**
 * A refusal by the host's network filter rather than by Giraffe: 403, no problem body, and a Content-Type that is
 * present and not JSON (nginx sends text/html). A 403 with no Content-Type at all stays a plain 403.
 */
export function isProxyBlockResponse(status: number, problem: Problem | undefined, contentType: string | null | undefined): boolean {
  return status === 403 && problem === undefined && !!contentType && !/json/i.test(contentType);
}

export function isApiProblem(error: unknown): error is ApiProblem {
  return error instanceof ApiProblem;
}

/** Best-effort parse of an error body into a Problem. */
export function toProblem(body: unknown): Problem | undefined {
  if (typeof body === "string") {
    try {
      return toProblem(JSON.parse(body));
    } catch {
      return undefined;
    }
  }
  if (typeof body === "object" && body !== null && "status" in body && "title" in body) {
    return body as Problem;
  }
  return undefined;
}

/** A sentence for an inline error block. `locale` picks the language of the messages that have one (default English). */
export function problemMessage(error: unknown, locale?: Locale): string {
  if (isApiProblem(error)) {
    if (error.isNetwork) return NETWORK_MESSAGE;
    // An Idempotency-Key duplicate still in progress after the retries (UX-01 §4.15): nothing is saved twice.
    if (error.isInProgress) return requestText("inProgress", locale);
    // The host network filter's HTML 403 names its likely cause, never "You do not have permission".
    if (error.isProxyBlocked) return systemParenText("proxyBlocked", locale);
    // SA-01 §5.1i: a UI-only action called with a token is plain 403 forbidden; its detail says why.
    if (error.status === 403) return error.problem?.detail ?? "You do not have permission for this action.";
    // A 500 tells the caller to quote the correlation id (ProblemMiddleware) but never showed it — the id is
    // already in the response body (ProblemWriter), just never read here.
    if (error.code === "internal" && error.problem?.correlationId) return `${error.message} (correlation ${error.problem.correlationId})`;
    // 2026-09-19 UX review finding 10: a caller with no bespoke off-state UI for this flag (LoadError, mostly)
    // fell through to error.message, which is the backend's own engineering-facing detail — literally
    // "The feature Features__ServiceDeskQueue is switched off." A screen that DOES render a proper UX-01 4.12
    // off-state never reaches problemMessage() for this case at all (it branches on isFeatureDisabled itself
    // before rendering an error), so this is purely the generic fallback for everywhere else.
    if (error.isFeatureDisabled) return "This isn't turned on for this project yet.";
    return error.message;
  }
  return error instanceof Error ? error.message : "Something went wrong.";
}

/** The server's own words for one field (problem-details `errors[]`), or undefined when it named none. */
export function fieldErrorText(error: unknown, field: string): string | undefined {
  if (!isApiProblem(error)) return undefined;
  const messages = error.fieldErrors.filter((fe) => fe.field === field).map((fe) => fe.message.trim()).filter((m) => m !== "");
  return messages.length > 0 ? [...new Set(messages)].join(" ") : undefined;
}

/**
 * problemMessage for a form submit: a validation refusal's `detail` is only the headline ("The partner organisation
 * could not be created."), and the reason lives in `errors[]` (R4-D01). The field messages follow the headline,
 * except those for `inlineFields` — the form shows those next to their input — and any the headline already says.
 */
export function formProblemMessage(error: unknown, locale?: Locale, inlineFields: readonly string[] = []): string {
  const base = problemMessage(error, locale);
  if (!isApiProblem(error) || error.isNetwork) return base;
  const extra: string[] = [];
  for (const fe of error.fieldErrors) {
    if (fe.field !== undefined && inlineFields.includes(fe.field)) continue;
    const message = fe.message.trim();
    if (message === "" || base.includes(message) || extra.includes(message)) continue;
    extra.push(message);
  }
  return [base, ...extra].join(" ");
}
